import * as i0 from '@angular/core';
import { InjectionToken, EventEmitter, forwardRef, ViewChild, Output, Input, Optional, Inject, ViewEncapsulation, Component, NgModule } from '@angular/core';
import * as i2 from '@angular/forms';
import { UntypedFormGroup, Validators, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i3 from '@engineering11/ui-lib/e11-input-label';
import { E11InputLabelModule } from '@engineering11/ui-lib/e11-input-label';

const TEXTAREA_CONFIG = new InjectionToken('TEXTAREA_CONFIG');
const DEFAULT_CONFIG = {
    maxLengthByContext: {},
    maxLengthWarningPercent: 90,
};

const DEFAULT_MAX_LENGTH = 524288;
class E11TextareaComponent {
    constructor(textareaConfig, cdr) {
        this.textareaConfig = textareaConfig;
        this.cdr = cdr;
        this.name = 'textarea';
        this.id = 'textarea_asNeeded';
        this.cols = 0;
        this.rows = 4;
        this.required = false;
        this.labelColor = '';
        this.borderGlow = '';
        this.readonly = false;
        this.disabled = false;
        this.hasError = false;
        this.parentForm = new UntypedFormGroup({});
        this.placeholder = '';
        this.darkMode = false;
        this.maxLength = DEFAULT_MAX_LENGTH;
        this.context = null;
        this.enforceCharacterLimit = false;
        this.showCharacterLimitWarning = true;
        this.formControlName = '';
        this.containerClass = '';
        this.inputClass = '';
        this.loading = false;
        this.labelTextOptional = '(Optional)';
        this.change = new EventEmitter();
        this.focus = new EventEmitter();
        this.blur = new EventEmitter();
        this.destroy$ = new Subject();
        this.borderGlowClass = '';
        this.inputValue = '';
        this.onChange = () => { };
        this.onTouch = () => { };
        this.reachingMaxLength = false;
        this.isFocused = false;
        if (!textareaConfig)
            this.textareaConfig = DEFAULT_CONFIG; // Enables the consuming application to not run .forRoot and still avoid runtime errors
    }
    ngOnInit() {
        if (this.borderGlow) {
            this.borderGlowClass = 'border-glow-' + this.borderGlow;
        }
        if (this.parentForm && this.formControlName) {
            const formControl = this.parentForm.controls[this.formControlName];
            formControl?.valueChanges?.pipe(takeUntil(this.destroy$))?.subscribe(() => {
                this.hasError = formControl.errors && (formControl.touched || formControl.dirty);
                if (this.maxLength && formControl.value?.length) {
                    const percentageOfInput = Math.floor((formControl.value.length / this.maxLength) * 100);
                    this.reachingMaxLength = this.showCharacterLimitWarning && percentageOfInput > (this.textareaConfig?.maxLengthWarningPercent ?? 0);
                }
            });
            if (this.context) {
                this.maxLength = this.textareaConfig.maxLengthByContext[this.context] ?? DEFAULT_MAX_LENGTH;
                formControl.addValidators([Validators.maxLength(this.maxLength)]);
                formControl.updateValueAndValidity();
            }
        }
    }
    ngOnDestroy() {
        if (this.destroy$ && this.destroy$.next) {
            this.destroy$.next(true);
            this.destroy$.unsubscribe();
        }
    }
    onFocus() {
        this.focus.emit();
        this.isFocused = true;
    }
    onBlur() {
        this.blur.emit();
        this.isFocused = false;
    }
    onInput(value) {
        const previousValue = this.inputValue;
        this.inputValue = value;
        if (previousValue !== value) {
            this.onChange(value);
            this.change.emit(value);
            this.cdr.markForCheck();
        }
    }
    writeValue(obj) {
        this.inputValue = obj ? obj : '';
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11TextareaComponent, deps: [{ token: TEXTAREA_CONFIG, optional: true }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11TextareaComponent, selector: "e11-textarea", inputs: { name: "name", id: "id", cols: "cols", rows: "rows", label: "label", required: "required", labelColor: "labelColor", borderGlow: "borderGlow", readonly: "readonly", disabled: "disabled", disabledText: "disabledText", hasError: "hasError", parentForm: "parentForm", placeholder: "placeholder", darkMode: "darkMode", maxLength: "maxLength", context: "context", enforceCharacterLimit: "enforceCharacterLimit", showCharacterLimitWarning: "showCharacterLimitWarning", formControlName: "formControlName", containerClass: "containerClass", inputClass: "inputClass", loading: "loading", tooltipConfig: "tooltipConfig", labelTextOptional: "labelTextOptional" }, outputs: { change: "change", focus: "focus", blur: "blur" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => E11TextareaComponent),
                multi: true,
            },
        ], viewQueries: [{ propertyName: "inputElement", first: true, predicate: ["textarea"], descendants: true }], ngImport: i0, template: "<div [formGroup]=\"parentForm\" class=\"e11-textarea-container {{ containerClass }}\" [attr.title]=\"disabled ? disabledText : null\">\n  <e11-input-label\n    [for]=\"id\"\n    [label]=\"label\"\n    [required]=\"required\"\n    [disabled]=\"disabled\"\n    [hasError]=\"hasError\"\n    [darkMode]=\"darkMode\"\n    [tooltipConfig]=\"tooltipConfig\"\n    [labelTextOptional]=\"labelTextOptional\"\n  ></e11-input-label>\n\n  <textarea\n    #textarea\n    (blur)=\"onBlur()\"\n    (input)=\"onInput($any($event.target).value)\"\n    (focus)=\"onFocus()\"\n    name=\"{{ name }}\"\n    [value]=\"inputValue\"\n    id=\"{{ id }}\"\n    cols=\"{{ cols }}\"\n    rows=\"{{ rows }}\"\n    role=\"textbox\"\n    class=\"e11-rounded-sm e11-border-gray-400 sm:e11-text-sm disabled:e11-opacity-50 disabled:e11-cursor-not-allowed {{ inputClass }}\"\n    [readonly]=\"readonly\"\n    [attr.disabled]=\"disabled ? disabled : null\"\n    [attr.maxLength]=\"enforceCharacterLimit ? maxLength : null\"\n    [placeholder]=\"loading ? '' : placeholder\"\n    [ngClass]=\"{\n      'e11-border-skin-info e11-text-skin-info focus:e11-ring-skin-info focus:e11-border-skin-info e11-pr-10':\n        !hasError && isFocused && reachingMaxLength,\n      'e11-border-skin-error e11-text-skin-error focus:e11-ring-skin-error focus:e11-border-skin-error e11-pr-10': hasError,\n      'e11-border-skin-grey focus:e11-ring-skin-primary-accent focus:e11-border-skin-primary-accent': !hasError,\n      'e11-border-skin-app-bg-dark focus:e11-ring-skin-app-bg-dark focus:e11-border-skin-app-bg-dark': !hasError && darkMode,\n      'e11-w-full': !cols || cols <= 0,\n      'e11-bg-skin-app-bg': disabled,\n      'e11-bg-skin-grey e11-text-skin-white e11-placeholder-white': darkMode,\n      'e11-skeleton e11-border-transparent e11-resize-none e11-cursor-wait': loading,\n    }\"\n  ></textarea>\n  <ng-content></ng-content>\n</div>\n", styles: ["::placeholder{color:red;opacity:1;font-style:italic}:-ms-input-placeholder{color:red;font-style:italic}::-ms-input-placeholder{color:red;font-style:italic}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "component", type: i3.E11InputLabelComponent, selector: "e11-input-label", inputs: ["label", "id", "for", "size", "labelClassOverrides", "required", "hasError", "disabled", "darkMode", "requiredMode", "labelTextOptional", "description", "tooltipConfig"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11TextareaComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-textarea', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => E11TextareaComponent),
                            multi: true,
                        },
                    ], encapsulation: ViewEncapsulation.None, template: "<div [formGroup]=\"parentForm\" class=\"e11-textarea-container {{ containerClass }}\" [attr.title]=\"disabled ? disabledText : null\">\n  <e11-input-label\n    [for]=\"id\"\n    [label]=\"label\"\n    [required]=\"required\"\n    [disabled]=\"disabled\"\n    [hasError]=\"hasError\"\n    [darkMode]=\"darkMode\"\n    [tooltipConfig]=\"tooltipConfig\"\n    [labelTextOptional]=\"labelTextOptional\"\n  ></e11-input-label>\n\n  <textarea\n    #textarea\n    (blur)=\"onBlur()\"\n    (input)=\"onInput($any($event.target).value)\"\n    (focus)=\"onFocus()\"\n    name=\"{{ name }}\"\n    [value]=\"inputValue\"\n    id=\"{{ id }}\"\n    cols=\"{{ cols }}\"\n    rows=\"{{ rows }}\"\n    role=\"textbox\"\n    class=\"e11-rounded-sm e11-border-gray-400 sm:e11-text-sm disabled:e11-opacity-50 disabled:e11-cursor-not-allowed {{ inputClass }}\"\n    [readonly]=\"readonly\"\n    [attr.disabled]=\"disabled ? disabled : null\"\n    [attr.maxLength]=\"enforceCharacterLimit ? maxLength : null\"\n    [placeholder]=\"loading ? '' : placeholder\"\n    [ngClass]=\"{\n      'e11-border-skin-info e11-text-skin-info focus:e11-ring-skin-info focus:e11-border-skin-info e11-pr-10':\n        !hasError && isFocused && reachingMaxLength,\n      'e11-border-skin-error e11-text-skin-error focus:e11-ring-skin-error focus:e11-border-skin-error e11-pr-10': hasError,\n      'e11-border-skin-grey focus:e11-ring-skin-primary-accent focus:e11-border-skin-primary-accent': !hasError,\n      'e11-border-skin-app-bg-dark focus:e11-ring-skin-app-bg-dark focus:e11-border-skin-app-bg-dark': !hasError && darkMode,\n      'e11-w-full': !cols || cols <= 0,\n      'e11-bg-skin-app-bg': disabled,\n      'e11-bg-skin-grey e11-text-skin-white e11-placeholder-white': darkMode,\n      'e11-skeleton e11-border-transparent e11-resize-none e11-cursor-wait': loading,\n    }\"\n  ></textarea>\n  <ng-content></ng-content>\n</div>\n", styles: ["::placeholder{color:red;opacity:1;font-style:italic}:-ms-input-placeholder{color:red;font-style:italic}::-ms-input-placeholder{color:red;font-style:italic}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [TEXTAREA_CONFIG]
                }] }, { type: i0.ChangeDetectorRef }], propDecorators: { name: [{
                type: Input
            }], id: [{
                type: Input
            }], cols: [{
                type: Input
            }], rows: [{
                type: Input
            }], label: [{
                type: Input
            }], required: [{
                type: Input
            }], labelColor: [{
                type: Input
            }], borderGlow: [{
                type: Input
            }], readonly: [{
                type: Input
            }], disabled: [{
                type: Input
            }], disabledText: [{
                type: Input
            }], hasError: [{
                type: Input
            }], parentForm: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], darkMode: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], context: [{
                type: Input
            }], enforceCharacterLimit: [{
                type: Input
            }], showCharacterLimitWarning: [{
                type: Input
            }], formControlName: [{
                type: Input
            }], containerClass: [{
                type: Input
            }], inputClass: [{
                type: Input
            }], loading: [{
                type: Input
            }], tooltipConfig: [{
                type: Input
            }], labelTextOptional: [{
                type: Input
            }], change: [{
                type: Output,
                args: ['change']
            }], focus: [{
                type: Output,
                args: ['focus']
            }], blur: [{
                type: Output,
                args: ['blur']
            }], inputElement: [{
                type: ViewChild,
                args: ['textarea', { static: false }]
            }] } });

class E11TextareaModule {
    static forRoot(config = DEFAULT_CONFIG) {
        return {
            ngModule: E11TextareaModule,
            providers: [{ provide: TEXTAREA_CONFIG, useValue: { ...DEFAULT_CONFIG, ...config } }],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11TextareaModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11TextareaModule, declarations: [E11TextareaComponent], imports: [CommonModule, ReactiveFormsModule, E11InputLabelModule], exports: [E11TextareaComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11TextareaModule, imports: [CommonModule, ReactiveFormsModule, E11InputLabelModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11TextareaModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [E11TextareaComponent],
                    imports: [CommonModule, ReactiveFormsModule, E11InputLabelModule],
                    exports: [E11TextareaComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { DEFAULT_CONFIG, E11TextareaComponent, E11TextareaModule, TEXTAREA_CONFIG };
